import { useMap } from "@vis.gl/react-google-maps";
import { useEffect } from "react";

// Renders the active search boundary on the map. Two shapes:
//   - 'circle':  coords is { center: { lat, lng }, radius: meters } — the
//     auto-boundary on Places autocomplete pick. Drawn as a circle so the
//     visual matches what the API actually filters on (D15 sends lat/lng +
//     radius for PostGIS distance check; the boundary is honest about the
//     query shape rather than showing the Places viewport rectangle while
//     filtering a circle).
//   - 'polygon': coords is Array<{ lat, lng }> — manual draw (P6.5/P6.6).
//     Stays visual-only per D17 rule 6; API filter remains circle-based
//     around the polygon's centroid.
//
// Imperative because @vis.gl/react-google-maps doesn't ship a declarative
// Circle / Polygon component — children of <Map> must drop down to the
// raw Google Maps API via useMap(). Lifecycle: build the overlay once per
// (shape, coord-identity) change, attach to the map on mount, drop it on
// cleanup.
//
// Stroke: navy (#002B49 = colors.accent.500 — kept as a literal because
// google.maps options take CSS color strings, not Chakra tokens).
// Translucent fill (10%) so adjacent listings stay visible beneath it.
const STROKE_COLOR = "#002B49";
const FILL_COLOR = "#002B49";
const FILL_OPACITY = 0.1;
const STROKE_WEIGHT = 2;
const STROKE_OPACITY = 0.9;

const BoundaryOverlay = ({ polygon }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !polygon) return;
        if (typeof window === "undefined" || !window.google?.maps) return;

        const { shape, coords } = polygon;
        let overlay = null;

        if (shape === "circle" && coords?.center && Number.isFinite(coords?.radius)) {
            overlay = new window.google.maps.Circle({
                center: coords.center,
                radius: coords.radius,
                strokeColor: STROKE_COLOR,
                strokeOpacity: STROKE_OPACITY,
                strokeWeight: STROKE_WEIGHT,
                fillColor: FILL_COLOR,
                fillOpacity: FILL_OPACITY,
                clickable: false,
                map,
            });
        } else if (shape === "polygon" && Array.isArray(coords) && coords.length >= 3) {
            overlay = new window.google.maps.Polygon({
                paths: coords,
                strokeColor: STROKE_COLOR,
                strokeOpacity: STROKE_OPACITY,
                strokeWeight: STROKE_WEIGHT,
                fillColor: FILL_COLOR,
                fillOpacity: FILL_OPACITY,
                clickable: false,
                map,
            });
        }

        return () => {
            if (overlay) overlay.setMap(null);
        };
    }, [map, polygon]);

    return null;
};

export default BoundaryOverlay;
